import { Maybe, Nullable } from '../../../../types';
import { MonitorPositionInfo } from '../../types';

export type OperatorType = {
  display: string;
  key: string;
};

export enum FilterConfigType {
  LOCATIONS,
  EVENTS,
}

export enum SubcriteriaEnum {
  Duration = 'Duration',
  AffectedHrs = 'Affected hrs',
  None = 'None',
}

export type ValueType = {
  key: string;
  display: string;
};

export type SeverityOptionsType = {
  critical: boolean;
  major: boolean;
  minor: boolean;
};

export type TimeType = {
  range: string;
  minutes: number;
  hours: number;
  days?: number;
};

export type RangeMinutes = {
  range: string;
  minutes: number;
};

export type PayloadTimeType = RangeMinutes | number;

export type AffectedHrsType = {
  value: TimeType;
  operator: OperatorType;
  criteriaType: 'affectedHrs';
};

export type DurationType = {
  value: number;
  operator: OperatorType;
  criteriaType: 'duration';
};

export type LocationType = {
  value: string;
  operator: OperatorType;
  criteriaType: string;
};

export type SubclauseType = {
  criteriaType: 'affectedHrs' | 'duration' | string;
  operator: string;
  selectedValue?: PayloadTimeType | string;
};

export enum CriteriaType {
  HealthScore = 'healthScore',
  SpecificIssue = 'specificIssue',
  IssueCategory = 'issueCategory',
  IncludeList = 'includeList',
  ExcludeList = 'excludeList',
}

export type ClauseType = {
  key?: string;
  criteriaType: CriteriaType;
  operator?: string;
  selectedValue: string | number;
  subcriteriaType?: string;
  subCriterias?: SubclauseType[];
  allowedValues?: string[];
  pattern?: string | null;
};

export type DisplayKeyPair = {
  display: string;
  key: string;
};

// Metadata
export type MonitorMetadata = {
  locationAttributes: LocationAttributesMetadata;
  signalIssues: SignalIssuesMetadata;
};

export type LocationAttributesMetadata = {
  metadata: LocationAttributeFilter[];
};

export type LocationAttributeFilter = {
  allowedValues: string[];
  attribute: string;
  category: string;
  columnName: string;
  dataType: FilterDataType;
  isBrowsable: boolean;
  pattern: Nullable<FilterPatternType>;
};

export type LocationAttributeFilterPayload = {
  columnName: string;
  columnValue: string;
  operator: string;
};

export enum FilterDataType {
  String = 'string',
  Time = 'time',
  StringArray = '[string]',
  Int = 'integer',
  Bool = 'boolean',
  BigInt = 'bigint',
  Float = 'float',
}

export enum FilterPatternType {
  String = 'string',
  Time = 'time',
  Numeric = 'numeric',
}

// TODO - look through and refactor
export type SignalIssuesMetadata = {
  metadata: {
    specificIssue: {
      displayName: string;
      selectedValueDatatype: string;
      allowedOperators: DisplayKeyPair[];
      allowedValues: DisplayKeyPair[];
      allowedSubCriterias: {
        duration: {
          required: boolean;
          displayName: string;
          selectedValueDatatype: string;
          allowedOperators: DisplayKeyPair[];
          allowedValues: DisplayKeyPair[];
        };
        affectedHrs: {
          required: boolean;
          displayName: string;
          selectedValueDatatype: string;
          allowedOperators: DisplayKeyPair[];
          selectedValueObjectAttributes: {
            minutes: {
              type: string;
              allowedValues: [];
            };
            range: {
              type: string;
              allowedValues: DisplayKeyPair[];
            };
          };
        };
      };
    };
    issueCategory: {
      displayName: string;
      selectedValueDatatype: string;
      allowedOperators: DisplayKeyPair[];
      allowedValues: DisplayKeyPair[];
    };
    healthScore: {
      displayName: string;
      selectedValueDatatype: string;
      allowedOperators: DisplayKeyPair[];
      allowedValues: DisplayKeyPair[];
    };
  };
  categoryMapping: {
    [key: string]: string[];
  };
  includeList: {
    selectedValueDatatype: string;
    displayName: string;
  };
  excludeList: {
    selectedValueDatatype: string;
    displayName: string;
  };
};

export type MonitorApiPayload = {
  name?: string;
  criteria?: {
    sequence: string;
    criterias: {
      [key: string]: ClauseType;
    };
  };
  harvestSyncEnabled?: boolean;
  locationAttributes?: LocationAttributes | undefined;
  monitorPositionInfo?: MonitorPositionInfo;
};

export type FilterSubcriteriaDefaultProps = {
  allowedOperators: DisplayKeyPair[];
  setValue: (data: SubclauseType) => void;
  initialData: Maybe<SubclauseType>;
};

export type DisplayMetadata = {
  categoryMapping: CategoryMapping[];
  specificIssue: SpecificIssue;
  issueCategory: SpecificIssue;
  healthScore: HealthScoreMeta;
};

export type CategoryMapping = {
  categoryName: string;
  displayName: string;
  items: IssueItem[];
};

export type IssueItem = ClauseType & {
  itemName: string;
  displayName: string;
  items?: IssueItem[];
};

export type SpecificIssue = {
  allowedValues: IssueItem[];
  allowedCriteria: IssueCriteria[];
};

export type HealthScoreMeta = {
  allowedOperators: IssueCriteria[];
  displayName: string;
};

export type IssueCriteria = {
  required: boolean;
  criteriaName: string;
  displayName: string;
  allowedOperators: DisplayKeyPair[];
};

export type LocationsDisplayMetadata = {
  categoryMapping: CategoryMapping;
  items: LocationsDisplayItem[];
};

export type LocationsDisplayItem = {
  itemName: string;
  allowedValues: string[];
  pattern: Nullable<string>;
  displayName: string;
  dataType: string;
};

export type LocationFilterType = LocationAttributeFilter & {
  columnValue: any;
  operator: string;
};

export enum Operator {
  OR = 'OR',
  AND = 'AND',
}

// ? What is this
export type LocationAttributes = {
  operator: Operator;
  filters: LocationAttributeFilterPayload[];
};

export type LocationAttributeMetadataType = {
  operator: Operator;
  filters: LocationFilterType[];
};

export type AllowedCriteriaNumeric = {
  equal: 'Equal';
  atLeast: 'At least';
  atMost: 'At most';
  moreThan: 'More than';
  lessThan: 'Less than';
  notEqualTo: 'Not equal to';
};
